import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ParsedBillData, OnboardingContext, OnboardingLevel } from '../models';
import { CommodityType, parseCommodityTypes } from '../../zentility/_shared/_zen-legacy-common/_models/commodity';

export interface CommodityBillExtraction {
  commodityType: CommodityType;
  data: ParsedBillData;
  /** Raw API extraction for this commodity — preserved for merge API and AI training */
  rawExtraction: import('./api-models').AiBillExtraction;
  /** Id of this commodity's AI request log row — sent back as the saved bill's billAiId */
  billAiId?: number;
  /** Position of this billing period when the PDF prints several for the commodity (1-based) */
  periodIndex?: number;
  /** How many billing periods the PDF prints for the commodity; undefined on a one-period bill */
  periodCount?: number;
}

export interface BillProcessingResult {
  /** Primary extraction (ELECTRICITY first on dual-fuel bills) — same as extractions[0].data */
  data: ParsedBillData;
  /** One entry per commodity found on the bill; dual-fuel PDFs have two */
  extractions: CommodityBillExtraction[];
  commodityTypes: CommodityType[];
  warnings: string[];
  /** Raw API extraction of the primary commodity — preserved for merge API and AI training */
  rawExtraction?: import('./api-models').AiBillExtraction;
  /** Server-side temporary file ID for the uploaded PDF */
  tempFileId?: string;
}
import { environment } from '../../../../environments/environment';
import { BillExtractionApiResponse, OnboardingDataType, EntityLevel, OnboardingRequestData } from './api-models';
import { BillMappingService } from './bill-mapping.service';
import { AiThinkingLevelService } from './ai-thinking-level.service';

/**
 * Extraction tracing — what the AI was asked for and what came back. Invaluable when a
 * real bill extracts wrong, but noise in a production console, so it is dev-only.
 * `console.error`/`console.warn` in this service stay unconditional by design.
 */
function traceExtraction(message: string, detail?: unknown): void {
  if (environment.production) {
    return;
  }
  if (detail === undefined) {
    console.log(message);
  } else {
    console.log(message, detail);
  }
}

/**
 * Service for processing utility bills using the AI Bill Extraction API
 * Replaces the old N8N webhook integration with direct API calls to /v4/ai/onboarding
 */
@Injectable({
  providedIn: 'root'
})
export class GeminiService {
  private readonly apiUrl: string;

  constructor(
    private http: HttpClient,
    private mappingService: BillMappingService,
    private thinkingLevelService: AiThinkingLevelService
  ) {
    this.apiUrl = `${environment.apiBaseUrl}/v4/ai/onboarding`;
  }

  /**
   * Process a bill PDF using the /v4/ai/onboarding API endpoint
   * @param file - The PDF file to process
   * @param isBudgetingMode - Whether to use budgeting/pro mode (detailed charge breakdowns)
   * @param context - Optional onboarding context for tiered extraction (performance optimization)
   * @returns Parsed bill data mapped to frontend model
   */
  async processBill(file: File, isBudgetingMode: boolean, context?: OnboardingContext): Promise<BillProcessingResult> {
    const dataType: OnboardingDataType = isBudgetingMode ? 'budgeting' : 'standard';
    const entityLevel = this.getEntityLevel(context);

    const formData = new FormData();

    // Build request data with entity level and pre-resolved IDs
    const thinkingLevels = this.thinkingLevelService.overrideFor(dataType);
    const requestData: OnboardingRequestData = {
      dataType,
      entityLevel,
      ...(thinkingLevels ? { thinkingLevels } : {}),
      ...this.buildEntityIds(context)
    };

    // Add the data part as a JSON Blob (matching Java controller expectations)
    formData.append('data', new Blob(
      [JSON.stringify(requestData)],
      { type: 'application/json' }
    ));

    // Add the PDF file
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    formData.append('file', file, safeName);

    try {
      traceExtraction(`[GeminiService] Processing bill: ${file.name} (${dataType} mode, entityLevel=${entityLevel}${thinkingLevels ? `, thinkingLevels=${JSON.stringify(thinkingLevels)}` : ''})`);

      // Call the API endpoint
      const response = await firstValueFrom(
        this.http.post<BillExtractionApiResponse>(this.apiUrl, formData)
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'API returned unsuccessful response');
      }

      traceExtraction('[GeminiService] API response received:', {
        model: response.model,
        tokens: response.tokens,
        entityLevel,
        hasData: !!response.data
      });

      // Map each commodity extraction to the frontend model. Older API responses
      // carry no extractions list — fall back to the primary data payload.
      const apiExtractions = (response.extractions?.length ? response.extractions : [{
        commodityType: response.commodityTypes?.[0],
        data: response.data,
        tokens: response.tokens,
        model: response.model,
        billAiId: undefined
      }]).filter(e => !!e.data);

      const extractions: CommodityBillExtraction[] = apiExtractions.map(e => {
        // The wrapper's commodity is the mapper's fallback — covers legacy
        // responses where data.commodityType is absent but commodityTypes says GAS.
        let parsed = this.mappingService.mapApiResponseToParsedBillData(e.data!, e.commodityType);
        parsed = this.applyKnownContext(parsed, context);
        return {
          commodityType: parsed.commodityType ?? CommodityType.Electricity,
          data: parsed,
          rawExtraction: e.data!,
          billAiId: e.billAiId ?? undefined,
          periodIndex: e.periodIndex ?? undefined,
          periodCount: e.periodCount ?? undefined
        };
      });

      if (!extractions.length) {
        throw new Error('API response contained no usable extraction data');
      }

      traceExtraction('[GeminiService] Parsed bill data:', {
        legalEntity: extractions[0].data.legalEntity,
        utilityName: extractions[0].data.utilityName,
        totalAmount: extractions[0].data.totalAmount,
        confidence: extractions[0].data.confidence,
        commodities: extractions.map(e => e.commodityType),
        warnings: response.warnings
      });

      return {
        data: extractions[0].data,
        extractions,
        commodityTypes: parseCommodityTypes(response.commodityTypes),
        warnings: response.warnings || [],
        rawExtraction: extractions[0].rawExtraction,
        tempFileId: response.tempFileId
      };

    } catch (error: any) {
      console.error('[GeminiService] Error processing bill:', error);

      // Handle specific error cases
      if (error.status === 0) {
        throw new Error('Network Error: Could not connect to API server. Check your internet connection.');
      }

      if (error.status === 401 || error.status === 403) {
        throw new Error('Authentication Error: Please log in again.');
      }

      if (error.status === 413) {
        throw new Error('File Too Large: The PDF file exceeds the 10MB limit.');
      }

      if (error.status === 500) {
        const serverMsg = error.error?.error?.replace(/^Extraction failed:\s*/i, '') || null;
        throw new Error(serverMsg || 'Server Error: The API encountered an error processing your bill. Please try again.');
      }

      // Re-throw with user-friendly message
      throw new Error(error.message || 'Failed to process bill. Please try again.');
    }
  }

  /**
   * Map OnboardingLevel to EntityLevel for the backend matching API.
   * Controls which hierarchical matching the backend runs.
   */
  private getEntityLevel(context?: OnboardingContext): EntityLevel {
    if (!context) {
      return 'organization';
    }

    switch (context.level) {
      case 'PORTFOLIO':
        return 'organization';
      case 'CUSTOMER':
        return 'customer';
      case 'LEN':
        return 'len';
      case 'SERVICE_ADDRESS':
        return 'serviceAddress';
      case 'METER':
        return 'meter';
      default:
        return 'organization';
    }
  }

  /**
   * Build entity ID fields for the request based on context.
   * These scope the backend matching to the known portion of the hierarchy.
   */
  private buildEntityIds(context?: OnboardingContext): Partial<Pick<OnboardingRequestData, 'customerId' | 'lenId' | 'serviceAddressId' | 'meterId'>> {
    if (!context) {
      return {};
    }

    const ids: Partial<Pick<OnboardingRequestData, 'customerId' | 'lenId' | 'serviceAddressId' | 'meterId'>> = {};

    if (context.customerId) {
      ids.customerId = parseInt(context.customerId, 10) || undefined;
    }

    if (context.lenId) {
      ids.lenId = context.lenId;
    }

    if (context.serviceAddressId) {
      ids.serviceAddressId = parseInt(context.serviceAddressId, 10) || undefined;
    }

    if (context.meterId) {
      ids.meterId = parseInt(context.meterId, 10) || undefined;
    }

    return ids;
  }

  /**
   * Apply known context values to parsed data.
   * This fills in fields that were skipped during extraction.
   */
  private applyKnownContext(parsedData: ParsedBillData, context?: OnboardingContext): ParsedBillData {
    if (!context) {
      return parsedData;
    }

    const result = { ...parsedData };

    // For LEN level and below, apply the known legal entity name
    if (context.level !== 'PORTFOLIO' && context.level !== 'CUSTOMER' && context.lenName) {
      // Only apply if not already extracted (empty or placeholder)
      if (!result.legalEntity || result.legalEntity === 'Unknown Entity') {
        result.legalEntity = context.lenName;
      }
    }

    // For SERVICE_ADDRESS level and below, apply the known address
    if ((context.level === 'SERVICE_ADDRESS' || context.level === 'METER') && context.serviceAddress) {
      // Only apply if not already extracted
      if (!result.serviceAddressStreet1 || result.serviceAddressStreet1 === 'Unknown Address') {
        result.serviceAddressStreet1 = context.serviceAddress.street1;
        result.serviceAddressStreet2 = context.serviceAddress.street2 || '';
        result.serviceAddressCity = context.serviceAddress.city;
        result.serviceAddressState = context.serviceAddress.state;
        result.serviceAddressZip = context.serviceAddress.zip;
      } else {
        // Partial parse: keep extracted fields, but inherit missing state/zip
        // so a not-extracted state isn't treated as a different address.
        if (!result.serviceAddressState) {
          result.serviceAddressState = context.serviceAddress?.state ?? '';
        }
        if (!result.serviceAddressZip) {
          result.serviceAddressZip = context.serviceAddress?.zip ?? '';
        }
      }
    }

    // For METER level, apply the known utility account number
    if (context.level === 'METER' && context.utilityAccountNumber) {
      if (!result.utilityAccountNumber) {
        result.utilityAccountNumber = context.utilityAccountNumber;
      }
    }

    return result;
  }


  /**
   * Placeholder for address lookup functionality
   * TODO: Implement Google Maps/Geocoding integration
   */
  async lookupAddress(query: string): Promise<{ address: Partial<ParsedBillData>, mapLink?: string }> {
    // Mock implementation - to be replaced with actual geocoding service
    console.warn('[GeminiService] lookupAddress not yet implemented');
    return { address: {} };
  }
}
